// src/pages/Tower/DeviceDashboardPage.jsx
import React, {
    useMemo,
    useState,
    useEffect,
    useRef,
    useCallback,
} from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Chip,
    IconButton,
    Tooltip,
    Divider,
    Stack,
    useTheme,
    alpha,
    MenuItem,
    Alert,
    Snackbar,
    Drawer,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    Skeleton,
} from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import * as Lucide from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';

import useTenantTelemetry from '../../hooks/useTenantTelemetry';

import Permitido from '../../security/Permitido';
import LiveBadge from '../../components/LiveBadge';
import { convertSensorDataToColor } from '../../infra/api/ColorConversionApi';
import { buildTelemetryApiConfig } from '../../config/sensorColorConfig';
import { xyYtoRgbNum, dominantWavelength } from '../../utils/colorConversions';
import { configurarSensorTorre, searchTowers } from '../../infra/api/Torres';
import { usePermissao } from '../../security/usePermissao';

// Visuais
import TowerSvgDiagram from '../../components/TowerSvgDiagram';
import SpectralBarChart from '../../components/modules/SpectralBarChart';
import CapacitiveSweepChart from '../../components/modules/CapacitiveSweepChart';
import ExportDataDialog from '../../components/modules/ExportDataDialog';
import {
    requestSaveHandle,
    writeBlobToHandle,
    downloadBlob,
    isAbortError,
} from '../../utils/saveFile';

const AS7341_CHANNELS = [
    'f1',
    'f2',
    'f3',
    'f4',
    'f5',
    'f6',
    'f7',
    'f8',
    'clear',
    'nir',
];
const APDS9960_CHANNELS = ['red', 'green', 'blue', 'clear'];
const APDS9960_KEY_MAP = { red: 'r', green: 'g', blue: 'b', clear: 'c' };

function tempColor(v) {
    if (v == null) return 'text.primary';
    if (v < 20) return '#1976d2';
    if (v < 30) return '#2e7d32';
    if (v < 40) return '#ed6c02';
    return '#d32f2f';
}

const MAX_HISTORY = 30;
const MAX_EVENT_LOG = 15;

/** Hook que mantém histórico de temperatura de forma imutável (Refatorado) */
function useTempHistory(temps) {
    const keys = useMemo(
        () => [
            'sample',
            'ambient',
            'core',
            'heatsinkUpper',
            'heatsinkLower',
            'sampleHeatExchanger',
            'magneticStirrer',
        ],
        []
    );

    const [history, setHistory] = useState(() =>
        Object.fromEntries(keys.map((k) => [k, []]))
    );

    useEffect(() => {
        if (!temps) return;
        const now = Date.now();

        setHistory((prev) => {
            const next = { ...prev };
            let hasChanges = false;

            for (const k of keys) {
                if (temps[k] != null) {
                    next[k] = [
                        ...prev[k].slice(-(MAX_HISTORY - 1)),
                        { t: now, v: temps[k] },
                    ];
                    hasChanges = true;
                }
            }
            return hasChanges ? next : prev;
        });
    }, [
        temps?.sample,
        temps?.ambient,
        temps?.core,
        temps?.heatsinkUpper,
        temps?.heatsinkLower,
        temps?.sampleHeatExchanger,
        temps?.magneticStirrer,
        keys,
    ]);

    return history;
}

/** Hook que mantém histórico de frequência de forma imutável (Refatorado) */
function useFreqHistory(freq) {
    const keys = useMemo(() => ['channel0', 'channel1'], []);
    const [history, setHistory] = useState(() =>
        Object.fromEntries(keys.map((k) => [k, []]))
    );

    useEffect(() => {
        if (!freq) return;
        const now = Date.now();

        setHistory((prev) => {
            const next = { ...prev };
            let hasChanges = false;

            for (const k of keys) {
                if (freq[k] != null) {
                    next[k] = [
                        ...prev[k].slice(-(MAX_HISTORY - 1)),
                        { t: now, v: freq[k] },
                    ];
                    hasChanges = true;
                }
            }
            return hasChanges ? next : prev;
        });
    }, [freq?.channel0, freq?.channel1, keys]);

    const stats = useMemo(() => {
        const result = {};
        for (const k of keys) {
            const vals = history[k].map((d) => d.v);
            if (vals.length < 2) {
                result[k] = null;
                continue;
            }
            const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
            const variance =
                vals.reduce((a, v) => a + (v - mean) ** 2, 0) / vals.length;
            result[k] = { mean, std: Math.sqrt(variance) };
        }
        return result;
    }, [history, keys]);

    return { history, stats };
}

/** Mini SVG sparkline inline otimizado com React.memo */
const Sparkline = React.memo(function Sparkline({
    data,
    color = '#1976d2',
    width = 80,
    height = 24,
}) {
    if (!data || data.length < 2) return null;
    const vals = data.map((d) => d.v);
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const range = max - min || 1;
    const points = vals
        .map((v, i) => {
            const x = (i / (vals.length - 1)) * width;
            const y = height - ((v - min) / range) * (height - 2) - 1;
            return `${x.toFixed(1)},${y.toFixed(1)}`;
        })
        .join(' ');

    return (
        <svg width={width} height={height} style={{ display: 'block' }}>
            <polyline
                points={points}
                fill="none"
                stroke={color}
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
});

/** Linha de métrica otimizada para evitar re-renders em cascata */
const MetricRow = React.memo(function MetricRow({
    label,
    value,
    unit,
    color,
    mono = true,
}) {
    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
            }}
        >
            <Typography variant="body2" color="text.secondary">
                {label}
            </Typography>
            <Typography
                variant="body2"
                fontWeight={700}
                fontFamily={mono ? 'monospace' : 'inherit'}
                color={color || 'text.primary'}
            >
                {value != null ? `${value}${unit ? ` ${unit}` : ''}` : '—'}
            </Typography>
        </Box>
    );
});

/** Placeholder de carregamento — espelha a estrutura real do dashboard
 * (diagrama + 3 cards + espectrometria) em vez de só um texto, pra dar uma
 * referência visual clara de que os dados estão a caminho. */
function DashboardSkeleton() {
    return (
        <Box>
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                <Grid item xs={12} md={5} lg={4}>
                    <Paper sx={{ p: 2, height: 320, borderRadius: 3 }}>
                        <Skeleton variant="text" width="40%" sx={{ mb: 1 }} />
                        <Skeleton
                            variant="rounded"
                            height={260}
                            sx={{ borderRadius: 2 }}
                        />
                    </Paper>
                </Grid>
                <Grid item xs={12} md={7} lg={8}>
                    <Grid container spacing={2.5}>
                        {[0, 1, 2].map((i) => (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                                <Paper sx={{ p: 2.5, height: 320, borderRadius: 3 }}>
                                    <Skeleton variant="text" width="55%" sx={{ mb: 1.5 }} />
                                    <Skeleton variant="text" width="35%" height={40} sx={{ mb: 1 }} />
                                    <Skeleton variant="rounded" height={24} sx={{ mb: 2 }} />
                                    <Skeleton variant="text" width="90%" />
                                    <Skeleton variant="text" width="80%" />
                                    <Skeleton variant="text" width="85%" />
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            </Grid>
            <Paper sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
                <Skeleton variant="text" width="20%" sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                    {[0, 1, 2].map((i) => (
                        <Grid item xs={12} md={4} key={i}>
                            <Skeleton variant="rounded" height={220} sx={{ borderRadius: 2 }} />
                        </Grid>
                    ))}
                </Grid>
            </Paper>
        </Box>
    );
}

const SPECTRAL_CHANNELS_AS7341 = [
    'f1',
    'f2',
    'f3',
    'f4',
    'f5',
    'f6',
    'f7',
    'f8',
];
const SPECTRAL_CHANNELS_APDS = ['red', 'green', 'blue', 'clear'];
const SAT_THRESHOLD = 65000;

function spectralStats(data) {
    if (!data) return null;
    const isAPDS =
        data?.sensorType === 'APDS9960' ||
        (data?.red != null && data?.f1 == null);
    const channels = isAPDS ? SPECTRAL_CHANNELS_APDS : SPECTRAL_CHANNELS_AS7341;
    const vals = channels.map((ch) => data[ch] ?? 0);
    const total = vals.reduce((a, b) => a + b, 0);
    const max = Math.max(...vals);
    const saturated = vals.filter((v) => v >= SAT_THRESHOLD).length;
    const nirClear =
        !isAPDS && data.clear && data.clear > 0
            ? (data.nir ?? 0) / data.clear
            : null;
    return { total, max, saturated, nirClear };
}

const NEPH_DARK_REFERENCE = {
    f1: 0,
    f2: 0,
    f3: 0,
    f4: 0,
    f5: 0,
    f6: 0,
    f7: 0,
    f8: 0,
    clear: 25,
    nir: 0,
    gain: 1,
    integrationTime: 100,
};

function useSensorColor(
    sensorData,
    measurementType = 'turbidimetry',
    turbidimetryData = null
) {
    const [color, setColor] = useState(null);
    const [opacity, setOpacity] = useState(1);
    const [xyYData, setXyYData] = useState(null);
    const [colorSpaces, setColorSpaces] = useState(null); // all color spaces from API
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState(null);
    const lastCallRef = React.useRef(0);

    useEffect(() => {
        if (!sensorData) {
            setStatus('idle');
            return;
        }

        const isAPDS = sensorData.sensorType === 'APDS9960';
        const channelsToCheck = isAPDS
            ? APDS9960_CHANNELS
            : ['f1', 'f2', 'f3', 'f4', 'f5', 'f6', 'f7', 'f8', 'clear'];
        const hasChannels = channelsToCheck.some(
            (ch) => sensorData[ch] != null && sensorData[ch] > 0
        );

        if (!hasChannels) {
            setStatus('idle');
            return;
        }

        const now = Date.now();
        if (now - lastCallRef.current < 3000) return;
        lastCallRef.current = now;

        if (!color) setStatus('loading');
        setError(null);

        const channels = isAPDS ? APDS9960_CHANNELS : AS7341_CHANNELS;
        const rawData = {};
        for (const ch of channels) {
            const key = isAPDS ? APDS9960_KEY_MAP[ch] || ch : ch;
            rawData[key] = sensorData[ch] != null ? [sensorData[ch]] : [0];
        }

        let referenceReading;
        if (measurementType === 'nephelometry' && turbidimetryData?.reference) {
            referenceReading = {};
            for (const ch of channels) {
                const key = isAPDS ? APDS9960_KEY_MAP[ch] || ch : ch;
                referenceReading[key] =
                    turbidimetryData.reference[ch] ??
                    turbidimetryData.reference[key] ??
                    0;
            }
            referenceReading.gain =
                turbidimetryData.reference.gain ?? turbidimetryData.gain ?? 1;
            referenceReading.integrationTime =
                turbidimetryData.reference.integrationTime ??
                turbidimetryData.reference.timeMs ??
                turbidimetryData.timeMs ??
                100;
        } else if (measurementType === 'nephelometry') {
            referenceReading = NEPH_DARK_REFERENCE;
        } else if (measurementType === 'fluorescence' && sensorData.reference) {
            referenceReading = {};
            for (const ch of channels) {
                const key = isAPDS ? APDS9960_KEY_MAP[ch] || ch : ch;
                referenceReading[key] =
                    sensorData.reference[ch] ?? sensorData.reference[key] ?? 0;
            }
            referenceReading.gain =
                sensorData.reference.gain ?? sensorData.gain ?? 1;
            referenceReading.integrationTime =
                sensorData.reference.integrationTime ??
                sensorData.reference.timeMs ??
                sensorData.timeMs ??
                100;
        } else if (
            sensorData.reference &&
            typeof sensorData.reference === 'object'
        ) {
            referenceReading = {};
            for (const ch of channels) {
                const key = isAPDS ? APDS9960_KEY_MAP[ch] || ch : ch;
                referenceReading[key] =
                    sensorData.reference[ch] ?? sensorData.reference[key] ?? 0;
            }
            referenceReading.gain =
                sensorData.reference.gain ?? sensorData.gain ?? 1;
            referenceReading.integrationTime =
                sensorData.reference.integrationTime ??
                sensorData.reference.timeMs ??
                sensorData.timeMs ??
                100;
        } else {
            setError('Referência de calibração não disponível no dispositivo');
            setStatus('error');
            return;
        }

        const payload = buildTelemetryApiConfig(measurementType, {
            targetColorSpaces: ['sRGB', 'LAB', 'XYZ', 'xyY', 'HSV', 'CMYK'],
            referenceReading,
        });
        payload.sensorType = isAPDS ? 'APDS9960' : 'AS7341';
        payload.rawData = {
            ...rawData,
            gain: sensorData.gain ?? 1,
            integrationTime: sensorData.timeMs ?? 100,
        };

        let cancelled = false;
        convertSensorDataToColor(payload)
            .then((res) => {
                if (cancelled) return;
                const results = res?.results || res?.convertedData || res;
                const xyY = results?.xyY?.[0];

                // Signal quality: check if API flagged the color as invalid
                const sq = results?.signalQuality;
                const displayColorValid =
                    sq?.displayColorValid ??
                    sq?.points?.[0]?.displayColorValid ??
                    true;

                if (xyY) {
                    setXyYData({ x: xyY.x ?? 0, y: xyY.y ?? 0, Y: xyY.Y ?? 0 });

                    // Store all color spaces for calibration (normalize keys)
                    const spaces = {};
                    if (results.xyY?.[0]) spaces.xyY = results.xyY[0];
                    if (results.XYZ?.[0]) {
                        const v = results.XYZ[0];
                        spaces.XYZ = {
                            X: v.X ?? v.x,
                            Y: v.Y ?? v.y,
                            Z: v.Z ?? v.z,
                        };
                    }
                    if (results.sRGB?.[0]) {
                        const v = results.sRGB[0];
                        spaces.sRGB = {
                            r: v.r ?? v.R,
                            g: v.g ?? v.G,
                            b: v.b ?? v.B,
                        };
                    }
                    if (results.LAB?.[0]) {
                        const v = results.LAB[0];
                        spaces.LAB = {
                            L: v.L ?? v.l,
                            a: v.a ?? v.A,
                            b: v.b ?? v.B,
                        };
                    }
                    if (results.HSV?.[0]) {
                        const v = results.HSV[0];
                        spaces.HSV = {
                            H: v.H ?? v.h,
                            S: v.S ?? v.s,
                            V: v.V ?? v.v,
                        };
                    }
                    if (results.CMYK?.[0]) {
                        const v = results.CMYK[0];
                        spaces.CMYK = {
                            C: v.C ?? v.c,
                            M: v.M ?? v.m,
                            Y: v.Y ?? v.y,
                            K: v.K ?? v.k,
                        };
                    }
                    setColorSpaces(spaces);

                    // If signal quality marks color as invalid, don't render swatch
                    if (!displayColorValid) {
                        setColor(null);
                        setStatus('ok');
                        return;
                    }

                    const [r, g, b] = xyYtoRgbNum(
                        xyY.x || 0,
                        xyY.y || 0,
                        1.0,
                        true
                    ).map((v) => Math.round(Math.max(0, Math.min(255, v))));

                    if (
                        (measurementType === 'nephelometry' ||
                            measurementType === 'fluorescence') &&
                        r === 0 &&
                        g === 0 &&
                        b === 0
                    ) {
                        setColor(null);
                        setStatus('idle');
                        return;
                    }

                    setColor(`rgb(${r},${g},${b})`);
                    setStatus('ok');

                    if (xyY?.Y != null) {
                        const Y = xyY.Y;
                        if (measurementType === 'turbidimetry') {
                            setOpacity(Math.max(0.15, Math.min(1, 1 - Y)));
                        } else {
                            setOpacity(Math.max(0.15, Math.min(1, Y)));
                        }
                    }
                } else {
                    setStatus('error');
                    setError('Resposta sem xyY');
                }
            })
            .catch((err) => {
                if (cancelled) return;
                setStatus('error');
                setError(err?.message || 'Erro na API spectral');
            });

        return () => {
            cancelled = true;
        };
    }, [sensorData, measurementType, turbidimetryData]);

    // Otimização Crucial: Evitar vazar novas referências de objeto a cada render
    return useMemo(
        () => ({ color, opacity, xyY: xyYData, colorSpaces, status, error }),
        [color, opacity, xyYData, colorSpaces, status, error]
    );
}

export default function DeviceDashboardPage({
    serialNumber: serialNumberProp,
    onBack: onBackProp,
}) {
    const theme = useTheme();
    const { serialNumber: serialNumberParam, tenant } = useParams();
    const navigate = useNavigate();
    // Suporta ser montado tanto via rota própria (/bioailabs/device/:serialNumber/dashboard
    // — sobrevive a F5, já que o serial fica na URL) quanto via prop direta
    // (uso legado). Sem rota nem prop não tem o que mostrar.
    const serialNumber = serialNumberProp ?? serialNumberParam;
    // Precisa do :tenant explícito no path — toda rota do app vive sob
    // /:tenant/* (App.jsx), então "/bioailabs" sozinho faria o router
    // reinterpretar "bioailabs" como se fosse o próprio tenant.
    const onBack = onBackProp ?? (() => navigate(`/${tenant}/bioailabs`));

    // Diagnóstico de engenharia (correntes Peltier/NEMA, malha de controle PID)
    // é telemetria técnica: visível só pra staff técnico. O dado continua
    // trafegando pra todos (o gate é só de apresentação — o "Últimos Logs",
    // derivado do controlState, segue visível pra qualquer usuário).
    const { pode } = usePermissao();
    const showEngineering = pode('dispositivo.engenharia');

    // Sem isso, o navigator preserva o scroll da lista de dispositivos (de onde
    // o usuário clicou "Telemetria"), então a página abre no meio em vez do topo.
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, [serialNumber]);

    const { connected, devicesBySerial, lastUpdated, reconnect, reconnecting } =
        useTenantTelemetry();
    const device = devicesBySerial[serialNumber] || null;

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const wasReconnecting = useRef(false);

    useEffect(() => {
        if (reconnecting) { wasReconnecting.current = true; }
        if (connected && wasReconnecting.current) {
            wasReconnecting.current = false;
            setSnackbar({ open: true, message: 'WebSocket reconectado com sucesso!', severity: 'success' });
        }
    }, [connected, reconnecting]);

    const handleReconnect = useCallback(() => {
        reconnect();
        setSnackbar({ open: true, message: 'Reconectando WebSocket...', severity: 'info' });
    }, [reconnect]);

    const temps = device?.temperatures;
    const power = device?.powerSupply;
    const peltier = device?.peltierCurrents;
    const nema = device?.nemaCurrents;
    const freq = device?.ressonantFrequencies;
    const control = device?.controlState;

    const isRunning = !!device?.experimentId;
    const deviceStatus =
        device?.status || (isRunning ? 'busy' : connected ? 'available' : null);

    const turb = useSensorColor(device?.spectral?.turbidimetry, 'turbidimetry');
    const neph = useSensorColor(
        device?.spectral?.nephelometry,
        'nephelometry',
        device?.spectral?.turbidimetry
    );
    const fluo = useSensorColor(
        device?.spectral?.fluorescence,
        'fluorescence',
        device?.spectral?.turbidimetry
    );

    const tempHistory = useTempHistory(temps);
    const { history: freqHistory, stats: freqStats } = useFreqHistory(freq);

    const watts = useMemo(
        () =>
            power?.voltage != null && power?.current != null
                ? power.voltage * power.current
                : null,
        [power?.voltage, power?.current]
    );

    const spectralSummary = useMemo(
        () => ({
            turbidimetry: spectralStats(device?.spectral?.turbidimetry),
            nephelometry: spectralStats(device?.spectral?.nephelometry),
            fluorescence: spectralStats(device?.spectral?.fluorescence),
        }),
        [
            device?.spectral?.turbidimetry,
            device?.spectral?.nephelometry,
            device?.spectral?.fluorescence,
        ]
    );

    const totalSaturated = useMemo(() => {
        return Object.values(spectralSummary).reduce(
            (acc, s) => acc + (s?.saturated || 0),
            0
        );
    }, [spectralSummary]);

    const lastTime = useMemo(() => {
        if (device?.timestamp) {
            return new Date(device.timestamp).toLocaleTimeString('pt-BR', {
                hour12: false,
            });
        }
        if (lastUpdated) {
            return lastUpdated.toLocaleTimeString('pt-BR', { hour12: false });
        }
        return null;
    }, [device?.timestamp, lastUpdated]);

    const diagnostics = useMemo(() => {
        const d = [];
        if (temps?.sample != null) {
            if (temps.sample > 45)
                d.push({
                    severity: 'error',
                    area: 'Temperatura',
                    msg: `Amostra crítica: ${temps.sample.toFixed(1)}°C`,
                });
            else if (temps.sample > 38)
                d.push({
                    severity: 'warning',
                    area: 'Temperatura',
                    msg: `Amostra elevada: ${temps.sample.toFixed(1)}°C`,
                });
        }
        if (temps?.core != null && temps.core > 50)
            d.push({
                severity: 'warning',
                area: 'Temperatura',
                msg: `Dissipador quente: ${temps.core.toFixed(1)}°C`,
            });
        if (
            temps?.heatsinkUpper != null &&
            temps?.heatsinkLower != null &&
            Math.abs(temps.heatsinkUpper - temps.heatsinkLower) > 15
        ) {
            d.push({
                severity: 'warning',
                area: 'Temperatura',
                msg: `Gradiente dissipadores: ${Math.abs(temps.heatsinkUpper - temps.heatsinkLower).toFixed(1)}°C`,
            });
        }
        if (temps?.magneticStirrer != null && temps.magneticStirrer > 45)
            d.push({
                severity: 'warning',
                area: 'Temperatura',
                msg: `Agitador quente: ${temps.magneticStirrer.toFixed(1)}°C`,
            });

        if (watts != null && watts > 60)
            d.push({
                severity: 'error',
                area: 'Elétrica',
                msg: `Potência crítica: ${watts.toFixed(1)} W`,
            });
        else if (watts != null && watts > 45)
            d.push({
                severity: 'warning',
                area: 'Elétrica',
                msg: `Potência elevada: ${watts.toFixed(1)} W`,
            });

        if (
            peltier?.sampleChamber != null &&
            Math.abs(peltier.sampleChamber) > 3
        )
            d.push({
                severity: 'warning',
                area: 'Elétrica',
                msg: `Peltier amostra: ${peltier.sampleChamber.toFixed(2)} A`,
            });
        if (
            peltier?.heatExchanger != null &&
            Math.abs(peltier.heatExchanger) > 3
        )
            d.push({
                severity: 'warning',
                area: 'Elétrica',
                msg: `Peltier dissipador: ${peltier.heatExchanger.toFixed(2)} A`,
            });

        if (
            control?.sampleTempError != null &&
            Math.abs(control.sampleTempError) > 5
        )
            d.push({
                severity: 'error',
                area: 'PID',
                msg: `Erro grande: ${control.sampleTempError.toFixed(1)}°C`,
            });
        else if (
            control?.sampleTempError != null &&
            Math.abs(control.sampleTempError) > 2
        )
            d.push({
                severity: 'info',
                area: 'PID',
                msg: `Estabilizando: erro ${control.sampleTempError.toFixed(1)}°C`,
            });

        if (totalSaturated > 0)
            d.push({
                severity: 'warning',
                area: 'Espectral',
                msg: `${totalSaturated} canal(is) saturado(s)`,
            });
        if (turb.status === 'error')
            d.push({
                severity: 'error',
                area: 'Cor',
                msg: `Turbidimetria: ${turb.error || 'erro'}`,
            });
        if (neph.status === 'error')
            d.push({
                severity: 'error',
                area: 'Cor',
                msg: `Nefelometria: ${neph.error || 'erro'}`,
            });
        if (fluo.status === 'error')
            d.push({
                severity: 'error',
                area: 'Cor',
                msg: `Fluorescência: ${fluo.error || 'erro'}`,
            });
        if (!connected)
            d.push({
                severity: 'error',
                area: 'Conexão',
                msg: 'Dispositivo desconectado',
            });
        return d;
    }, [
        temps?.sample,
        temps?.core,
        temps?.heatsinkUpper,
        temps?.heatsinkLower,
        temps?.magneticStirrer,
        watts,
        peltier?.sampleChamber,
        peltier?.heatExchanger,
        control?.sampleTempError,
        totalSaturated,
        turb.status,
        turb.error,
        neph.status,
        neph.error,
        fluo.status,
        fluo.error,
        connected,
    ]);

    const logRef = useRef([]);
    const [eventLog, setEventLog] = useState([]);
    useEffect(() => {
        if (diagnostics.length === 0) return;
        const now = new Date();
        const ts = now.toLocaleTimeString('pt-BR', { hour12: false });
        const newEntries = diagnostics
            .filter((d) => d.severity === 'error' || d.severity === 'warning')
            .map((d) => ({ ts, severity: d.severity, msg: d.msg }));

        if (newEntries.length > 0) {
            const prev = logRef.current;
            const deduped = newEntries.filter(
                (ne) => !prev.length || prev[prev.length - 1]?.msg !== ne.msg
            );
            if (deduped.length > 0) {
                logRef.current = [...prev, ...deduped].slice(-MAX_EVENT_LOG);
                setEventLog([...logRef.current]);
            }
        }
    }, [diagnostics]);

    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [exportSnapshot, setExportSnapshot] = useState(null);
    const [pdfGenerating, setPdfGenerating] = useState(false);
    const [calibDrawerOpen, setCalibDrawerOpen] = useState(false);
    const [sensorMeasurement, setSensorMeasurement] = useState('turbidimetry');
    const [sensorGain, setSensorGain] = useState('');
    const [sensorIntegrationTimeMs, setSensorIntegrationTimeMs] = useState('');
    const [sensorConfigSaving, setSensorConfigSaving] = useState(false);
    const [sensorModeIsManual, setSensorModeIsManual] = useState(false);
    const dashboardRef = useRef(null);

    // Mede a altura real do card do diagrama (BioAiLab) pra travar a altura
    // máxima do card "Últimos Logs" nela — sem isso, uma lista de logs longa
    // faz o card de logs crescer, e como os dois ficam na mesma linha do
    // Grid (que estica os itens pra bater a altura do maior), o card do SVG
    // é "puxado" junto, ocupando espaço vertical desproporcional na tela.
    const bioAiLabCardRef = useRef(null);
    const [logsMaxHeight, setLogsMaxHeight] = useState(null);

    useEffect(() => {
        const el = bioAiLabCardRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(([entry]) => {
            if (entry) setLogsMaxHeight(entry.contentRect.height);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const fillSensorConfigFromMeasurement = useCallback(
        (measurement, towerSensorConfig) => {
            const cfg = towerSensorConfig?.[measurement];
            setSensorGain(cfg?.gain != null ? String(cfg.gain) : '');
            setSensorIntegrationTimeMs(
                cfg?.integrationTimeMs != null ? String(cfg.integrationTimeMs) : ''
            );
            // A presença de sensorConfig.<measurement> na torre é o sinal de que esse
            // canal está travado em manual — quando volta pro automático, o backend
            // remove esse campo (ver TorreService.configurarSensorPorNumeroSerie).
            setSensorModeIsManual(cfg != null);
        },
        []
    );

    const handleOpenCalibDrawer = useCallback(async () => {
        setCalibDrawerOpen(true);
        try {
            const results = await searchTowers(serialNumber);
            const tower = Array.isArray(results)
                ? results.find((t) => t.numeroSerie === serialNumber)
                : null;
            fillSensorConfigFromMeasurement(sensorMeasurement, tower?.sensorConfig);
        } catch (err) {
            // Pré-preenchimento é só conveniência — se falhar, o form fica em branco.
        }
    }, [serialNumber, sensorMeasurement, fillSensorConfigFromMeasurement]);

    const handleSensorMeasurementChange = useCallback(
        async (measurement) => {
            setSensorMeasurement(measurement);
            try {
                const results = await searchTowers(serialNumber);
                const tower = Array.isArray(results)
                    ? results.find((t) => t.numeroSerie === serialNumber)
                    : null;
                fillSensorConfigFromMeasurement(measurement, tower?.sensorConfig);
            } catch (err) {
                setSensorGain('');
                setSensorIntegrationTimeMs('');
                setSensorModeIsManual(false);
            }
        },
        [serialNumber, fillSensorConfigFromMeasurement]
    );

    const handleApplySensorConfig = useCallback(async () => {
        const gainNum = Number(sensorGain);
        const timeNum = Number(sensorIntegrationTimeMs);
        if (!Number.isFinite(gainNum) || !Number.isFinite(timeNum)) {
            setSnackbar({
                open: true,
                message: 'Informe ganho e tempo de integração válidos.',
                severity: 'error',
            });
            return;
        }

        setSensorConfigSaving(true);
        try {
            await configurarSensorTorre(serialNumber, {
                measurement: sensorMeasurement,
                mode: 'manual',
                gain: gainNum,
                integrationTimeMs: timeNum,
            });
            setSnackbar({
                open: true,
                message:
                    'Configuração enviada. Só terá efeito se a torre tiver um tubo inserido — o valor fica travado em manual até você clicar em "Voltar para Automático".',
                severity: 'success',
            });
            setSensorModeIsManual(true);
            setCalibDrawerOpen(false);
        } catch (err) {
            setSnackbar({
                open: true,
                message: err?.message || 'Falha ao enviar configuração do sensor.',
                severity: 'error',
            });
        } finally {
            setSensorConfigSaving(false);
        }
    }, [serialNumber, sensorMeasurement, sensorGain, sensorIntegrationTimeMs]);

    const handleResetSensorAuto = useCallback(async () => {
        setSensorConfigSaving(true);
        try {
            await configurarSensorTorre(serialNumber, {
                measurement: sensorMeasurement,
                mode: 'auto',
            });
            setSnackbar({
                open: true,
                message:
                    'Travamento manual removido. A calibração automática valerá de novo a partir da próxima troca de tubo.',
                severity: 'success',
            });
            setSensorModeIsManual(false);
            setSensorGain('');
            setSensorIntegrationTimeMs('');
        } catch (err) {
            setSnackbar({
                open: true,
                message: err?.message || 'Falha ao voltar para o modo automático.',
                severity: 'error',
            });
        } finally {
            setSensorConfigSaving(false);
        }
    }, [serialNumber, sensorMeasurement]);

    // Monta objeto de colorimetria para calibração
    const colorimetryData = useMemo(
        () => ({
            turbidimetry: turb,
            nephelometry: neph,
            fluorescence: fluo,
        }),
        [turb, neph, fluo]
    );

    const buildSnapshot = useCallback(() => {
        const ts = new Date().toISOString();
        return {
            serialNumber,
            timestamp: ts,
            firmwareVersion: device?.firmwareVersion || null,
            status: deviceStatus,
            temperatures: temps ? { ...temps } : null,
            powerSupply: power ? { ...power, watts } : null,
            peltierCurrents: peltier ? { ...peltier } : null,
            nemaCurrents: nema ? { ...nema } : null,
            ressonantFrequencies: freq ? { ...freq } : null,
            controlState: control ? { ...control } : null,
            colorimetry: {
                turbidimetry: turb.xyY
                    ? { ...turb.xyY, color: turb.color, opacity: turb.opacity }
                    : null,
                nephelometry: neph.xyY
                    ? { ...neph.xyY, color: neph.color, opacity: neph.opacity }
                    : null,
                fluorescence: fluo.xyY
                    ? { ...fluo.xyY, color: fluo.color, opacity: fluo.opacity }
                    : null,
            },
            spectral: {
                turbidimetry: device?.spectral?.turbidimetry || null,
                nephelometry: device?.spectral?.nephelometry || null,
                fluorescence: device?.spectral?.fluorescence || null,
            },
            diagnostics,
        };
    }, [
        serialNumber,
        device,
        deviceStatus,
        temps,
        power,
        watts,
        peltier,
        nema,
        freq,
        control,
        turb,
        neph,
        fluo,
        diagnostics,
    ]);

    const handleOpenExportDialog = useCallback(() => {
        setExportSnapshot(buildSnapshot());
        setExportDialogOpen(true);
    }, [buildSnapshot]);

    const handleExportPDF = useCallback(async () => {
        if (!dashboardRef.current) return;

        const filename = `telemetria_${serialNumber}_${new Date()
            .toISOString()
            .slice(0, 19)
            .replace(/:/g, '-')}.pdf`;

        // Pergunta ONDE salvar ANTES de gerar: showSaveFilePicker() exige a
        // ativacao recente do clique (~5s) e o html2canvas pode demorar mais
        // que isso — pedindo o destino primeiro, o dialogo nunca e recusado.
        let handle = null;
        try {
            handle = await requestSaveHandle(filename, {
                description: 'Telemetria (PDF)',
                mime: 'application/pdf',
                ext: 'pdf',
            });
        } catch (e) {
            if (isAbortError(e)) return; // usuario cancelou -> nao gera nada
            handle = null; // picker indisponivel -> cai no download tradicional
        }

        setPdfGenerating(true);
        try {
            const { default: html2canvas } = await import('html2canvas');
            const { jsPDF } = await import('jspdf');
            const canvas = await html2canvas(dashboardRef.current, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
            });
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4',
            });
            const pdfW = pdf.internal.pageSize.getWidth();
            const pdfH = pdf.internal.pageSize.getHeight();
            const imgW = canvas.width;
            const imgH = canvas.height;
            const ratio = Math.min(pdfW / imgW, pdfH / imgH);
            const w = imgW * ratio;
            const h = imgH * ratio;
            pdf.addImage(imgData, 'PNG', (pdfW - w) / 2, 2, w, h);

            const blob = pdf.output('blob');
            if (handle) {
                await writeBlobToHandle(handle, blob);
            } else {
                downloadBlob(blob, filename);
            }

            setExportDialogOpen(false);
            setSnackbar({
                open: true,
                message: handle ? 'PDF salvo com sucesso!' : 'PDF gerado com sucesso!',
                severity: 'success',
            });
        } catch (e) {
            setSnackbar({ open: true, message: 'Falha ao gerar o PDF.', severity: 'error' });
        } finally {
            setPdfGenerating(false);
        }
    }, [serialNumber]);

    const dashboardContent = (
        <Box sx={{ p: { xs: 1.5, md: 2 }, width: '100%' }} ref={dashboardRef}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 3,
                    gap: 2,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        width: { xs: '100%', sm: 'auto' },
                        justifyContent: { xs: 'center', sm: 'flex-start' },
                    }}
                >
                    <Tooltip title="Voltar para a lista">
                        <IconButton
                            onClick={() => onBack?.()}
                            sx={{ bgcolor: 'action.hover' }}
                        >
                            <Lucide.ArrowLeft />
                        </IconButton>
                    </Tooltip>
                    <Box>
                        <Typography
                            variant="h5"
                            fontWeight={800}
                            sx={{ fontFamily: 'monospace', letterSpacing: 1 }}
                        >
                            {serialNumber}
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mt: 0.5,
                                flexWrap: 'wrap',
                                justifyContent: { xs: 'center', sm: 'flex-start' },
                            }}
                        >
                            <LiveBadge connected={connected} />
                            <Tooltip title="Reconectar WebSocket">
                                <IconButton
                                    size="small"
                                    onClick={handleReconnect}
                                    color={connected ? 'success' : 'warning'}
                                >
                                    <Lucide.RefreshCw size={18} />
                                </IconButton>
                            </Tooltip>
                            {device?.firmwareVersion && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                    }}
                                >
                                    <Lucide.MemoryStick size={14} /> v
                                    {device.firmwareVersion}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                        width: { xs: '100%', sm: 'auto' },
                        justifyContent: 'center',
                    }}
                >
                    {lastTime && (
                        <Typography variant="body2" color="text.secondary">
                            Atualizado às <b>{lastTime}</b>
                        </Typography>
                    )}
                    {deviceStatus && (
                        <Chip
                            label={
                                deviceStatus === 'busy'
                                    ? 'ENSAIO EM ANDAMENTO'
                                    : deviceStatus === 'locked'
                                      ? 'SISTEMA BLOQUEADO'
                                      : deviceStatus === 'available'
                                        ? 'SISTEMA DISPONÍVEL'
                                        : deviceStatus.toUpperCase()
                            }
                            color={
                                deviceStatus === 'busy'
                                    ? 'info'
                                    : deviceStatus === 'locked'
                                      ? 'warning'
                                      : 'success'
                            }
                            variant="filled"
                            sx={{ fontWeight: 'bold', borderRadius: 2 }}
                        />
                    )}

                    <Tooltip title="Configurar sensor manualmente">
                        <IconButton
                            onClick={handleOpenCalibDrawer}
                            sx={{ bgcolor: 'action.hover' }}
                        >
                            <Lucide.Settings />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Exportar dados">
                        <IconButton
                            onClick={handleOpenExportDialog}
                            sx={{ bgcolor: 'action.hover' }}
                        >
                            <Lucide.Download />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            <ExportDataDialog
                open={exportDialogOpen}
                onClose={() => setExportDialogOpen(false)}
                snapshot={exportSnapshot}
                serialNumber={serialNumber}
                onDownloadPDF={handleExportPDF}
                pdfGenerating={pdfGenerating}
                onSaved={(filename, err) =>
                    setSnackbar({
                        open: true,
                        message: err
                            ? 'Falha ao salvar o arquivo.'
                            : `Arquivo ${filename} salvo com sucesso!`,
                        severity: err ? 'error' : 'success',
                    })
                }
            />

            {!device && (
                <Box>
                    <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>
                        Aguardando sincronização de telemetria do dispositivo{' '}
                        <b>{serialNumber}</b>
                        {!connected && ' — reconectando ao WebSocket...'}
                    </Alert>
                    <DashboardSkeleton />
                </Box>
            )}

            {device && !connected && (
                <Alert
                    severity="warning"
                    sx={{ mb: 2.5, borderRadius: 2 }}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={handleReconnect}
                            startIcon={<Lucide.RefreshCw size={18} />}
                        >
                            Reconectar
                        </Button>
                    }
                >
                    Conexão em tempo real perdida — mostrando os últimos dados
                    recebidos{lastTime ? ` às ${lastTime}` : ''}. Tentando
                    reconectar automaticamente...
                </Alert>
            )}

            {device && (
                <>
                    {diagnostics.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            {diagnostics.map((d, i) => (
                                <Alert
                                    key={i}
                                    severity={d.severity}
                                    sx={{ mb: 1, borderRadius: 2 }}
                                    icon={
                                        d.severity === 'error' ? (
                                            <Lucide.TriangleAlert />
                                        ) : undefined
                                    }
                                >
                                    <b>{d.area}:</b> {d.msg}
                                </Alert>
                            ))}
                        </Box>
                    )}

                    {/* alignItems: 'flex-start' é essencial aqui — sem isso o Grid
                        estica o card do SVG (height:100%) pra bater a altura da coluna
                        vizinha, que inclui o card de Últimos Logs. Como a altura desse
                        card é travada na altura MEDIDA do card do SVG, isso criava um
                        ciclo: logs crescem -> linha do Grid cresce -> SVG estica junto
                        -> altura medida aumenta -> teto dos logs aumenta -> logs crescem
                        mais. Com flex-start, o card do SVG fica na sua altura natural,
                        independente da coluna ao lado. */}
                    <Grid
                        container
                        spacing={2.5}
                        sx={{ mb: 3, alignItems: 'flex-start' }}
                    >
                        <Grid item xs={12} md={5} lg={4}>
                            <Paper
                                ref={bioAiLabCardRef}
                                sx={{
                                    p: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRadius: 3,
                                }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    fontWeight={700}
                                    color="text.secondary"
                                    sx={{ mb: 1 }}
                                >
                                    BioAiLab
                                </Typography>
                                <Box
                                    sx={{
                                        flexGrow: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <TowerSvgDiagram
                                        temps={temps}
                                        sensorColors={[turb, neph, fluo]}
                                        spectral={device?.spectral}
                                        freq={freq}
                                        isOnline={connected}
                                        status={deviceStatus}
                                        hasProtocol={device?.status === 'busy'}
                                    />
                                </Box>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={7} lg={8}>
                            <Grid container spacing={2.5}>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Paper
                                        sx={{
                                            p: 2.5,
                                            height: '100%',
                                            borderRadius: 3,
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle2"
                                            fontWeight={700}
                                            color="text.secondary"
                                            gutterBottom
                                        >
                                            Temperaturas
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'baseline',
                                                gap: 1,
                                                mb: 1,
                                            }}
                                        >
                                            <Typography
                                                variant="h3"
                                                fontWeight={800}
                                                sx={{
                                                    color: tempColor(
                                                        temps?.sample
                                                    ),
                                                    lineHeight: 1,
                                                }}
                                            >
                                                {temps?.sample != null
                                                    ? `${temps.sample.toFixed(1)}°`
                                                    : '—'}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                amostra
                                            </Typography>
                                        </Box>
                                        <Sparkline
                                            data={tempHistory.sample}
                                            color={tempColor(temps?.sample)}
                                            width={120}
                                            height={20}
                                        />
                                        <Divider sx={{ my: 1.5 }} />
                                        <Stack spacing={0.8}>
                                            {[
                                                {
                                                    label: 'Ambiente',
                                                    key: 'ambient',
                                                    color: '#9e9e9e',
                                                },
                                                {
                                                    label: 'Núcleo',
                                                    key: 'core',
                                                    color: '#ff7043',
                                                },
                                                {
                                                    label: 'Dissipador Superior',
                                                    key: 'heatsinkUpper',
                                                    color: '#42a5f5',
                                                },
                                                {
                                                    label: 'Dissipador Inferior',
                                                    key: 'heatsinkLower',
                                                    color: '#5c6bc0',
                                                },
                                                {
                                                    label: 'Trocador de Calor',
                                                    key: 'sampleHeatExchanger',
                                                    color: '#ab47bc',
                                                },
                                                {
                                                    label: 'Agitador Magnético',
                                                    key: 'magneticStirrer',
                                                    color: '#26a69a',
                                                },
                                            ]
                                                .filter(
                                                    ({ key }) =>
                                                        temps?.[key] != null
                                                )
                                                .map(
                                                    ({ label, key, color }) => (
                                                        <Box
                                                            key={key}
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent:
                                                                    'space-between',
                                                                alignItems:
                                                                    'center',
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                            >
                                                                {label}
                                                            </Typography>
                                                            <Box
                                                                sx={{
                                                                    display:
                                                                        'flex',
                                                                    alignItems:
                                                                        'center',
                                                                    gap: 1,
                                                                }}
                                                            >
                                                                <Sparkline
                                                                    data={
                                                                        tempHistory[
                                                                            key
                                                                        ]
                                                                    }
                                                                    color={
                                                                        color
                                                                    }
                                                                    width={50}
                                                                    height={14}
                                                                />
                                                                <Typography
                                                                    variant="body2"
                                                                    fontWeight={
                                                                        700
                                                                    }
                                                                    fontFamily="monospace"
                                                                >
                                                                    {temps[
                                                                        key
                                                                    ].toFixed(
                                                                        1
                                                                    )}
                                                                    °C
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    )
                                                )}
                                        </Stack>
                                    </Paper>
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                    <Paper
                                        sx={{
                                            p: 2.5,
                                            height: '100%',
                                            borderRadius: 3,
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle2"
                                            fontWeight={700}
                                            color="text.secondary"
                                            gutterBottom
                                        >
                                            Colorimetria
                                        </Typography>
                                        <Stack spacing={1.5}>
                                            {[
                                                {
                                                    label: 'Turbidimetria',
                                                    data: turb,
                                                    type: 'turbidimetry',
                                                },
                                                {
                                                    label: 'Nefelometria',
                                                    data: neph,
                                                    type: 'nephelometry',
                                                },
                                                {
                                                    label: 'Fluorescência',
                                                    data: fluo,
                                                    type: 'fluorescence',
                                                },
                                            ].map(
                                                ({ label, data: s, type }) => {
                                                    const lambda = s.xyY
                                                        ? dominantWavelength(
                                                              s.xyY.x,
                                                              s.xyY.y
                                                          )
                                                        : null;
                                                    const lowSignal =
                                                        s.xyY && s.xyY.Y < 0.01;
                                                    return (
                                                        <Box key={label}>
                                                            {s.status ===
                                                                'ok' &&
                                                            s.color ? (
                                                                <Box>
                                                                    <Box
                                                                        sx={{
                                                                            display:
                                                                                'flex',
                                                                            alignItems:
                                                                                'center',
                                                                            gap: 1,
                                                                            mb: 0.5,
                                                                        }}
                                                                    >
                                                                        <Box
                                                                            sx={{
                                                                                width: 16,
                                                                                height: 16,
                                                                                borderRadius: 1,
                                                                                flexShrink: 0,
                                                                                bgcolor:
                                                                                    s.color,
                                                                                border: '1px solid',
                                                                                borderColor:
                                                                                    'divider',
                                                                            }}
                                                                        />
                                                                        <Typography
                                                                            variant="body2"
                                                                            fontWeight={
                                                                                600
                                                                            }
                                                                            lineHeight={
                                                                                1
                                                                            }
                                                                        >
                                                                            {
                                                                                label
                                                                            }
                                                                        </Typography>
                                                                        {lambda && (
                                                                            <Chip
                                                                                label={`λ ${lambda} nm`}
                                                                                size="small"
                                                                                variant="outlined"
                                                                                sx={{
                                                                                    height: 18,
                                                                                    fontSize: 10,
                                                                                    ml: 'auto',
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </Box>
                                                                    {s.xyY && (
                                                                        <Box
                                                                            sx={{
                                                                                pl: 3,
                                                                            }}
                                                                        >
                                                                            <Typography
                                                                                variant="caption"
                                                                                fontFamily="monospace"
                                                                                color="text.secondary"
                                                                                lineHeight={
                                                                                    1.4
                                                                                }
                                                                            >
                                                                                x=
                                                                                {s.xyY.x.toFixed(
                                                                                    4
                                                                                )}{' '}
                                                                                y=
                                                                                {s.xyY.y.toFixed(
                                                                                    4
                                                                                )}
                                                                            </Typography>
                                                                            <Box
                                                                                sx={{
                                                                                    display:
                                                                                        'flex',
                                                                                    alignItems:
                                                                                        'center',
                                                                                    gap: 1,
                                                                                }}
                                                                            >
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    fontFamily="monospace"
                                                                                    color="text.secondary"
                                                                                    lineHeight={
                                                                                        1.2
                                                                                    }
                                                                                    sx={{
                                                                                        minWidth: 62,
                                                                                    }}
                                                                                >
                                                                                    Y=
                                                                                    {s.xyY.Y.toFixed(
                                                                                        4
                                                                                    )}
                                                                                </Typography>
                                                                                <LinearProgress
                                                                                    variant="determinate"
                                                                                    value={Math.min(
                                                                                        100,
                                                                                        Math.max(
                                                                                            0,
                                                                                            (type ===
                                                                                            'turbidimetry'
                                                                                                ? 1 -
                                                                                                  s
                                                                                                      .xyY
                                                                                                      .Y
                                                                                                : s
                                                                                                      .xyY
                                                                                                      .Y) *
                                                                                                100
                                                                                        )
                                                                                    )}
                                                                                    sx={{
                                                                                        flex: 1,
                                                                                        height: 4,
                                                                                        borderRadius: 2,
                                                                                        bgcolor:
                                                                                            'action.hover',
                                                                                        '& .MuiLinearProgress-bar':
                                                                                            {
                                                                                                bgcolor:
                                                                                                    s.color,
                                                                                                borderRadius: 2,
                                                                                            },
                                                                                    }}
                                                                                />
                                                                            </Box>
                                                                            {lowSignal && (
                                                                                <Box
                                                                                    sx={{
                                                                                        display:
                                                                                            'flex',
                                                                                        alignItems:
                                                                                            'center',
                                                                                        gap: 0.5,
                                                                                        mt: 0.3,
                                                                                    }}
                                                                                >
                                                                                    <Lucide.TriangleAlert
                                                                                        size={12}
                                                                                        color={theme.palette.warning.main}
                                                                                    />
                                                                                    <Typography
                                                                                        variant="caption"
                                                                                        color="warning.main"
                                                                                        fontSize={
                                                                                            10
                                                                                        }
                                                                                    >
                                                                                        Sinal
                                                                                        muito
                                                                                        baixo
                                                                                    </Typography>
                                                                                </Box>
                                                                            )}
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            ) : s.status ===
                                                              'loading' ? (
                                                                <Box
                                                                    sx={{
                                                                        display:
                                                                            'flex',
                                                                        alignItems:
                                                                            'center',
                                                                        gap: 1,
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            width: 16,
                                                                            height: 16,
                                                                            borderRadius: 1,
                                                                            bgcolor:
                                                                                'action.hover',
                                                                            flexShrink: 0,
                                                                        }}
                                                                    />
                                                                    <Typography
                                                                        variant="body2"
                                                                        color="text.secondary"
                                                                    >
                                                                        {label}
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.disabled"
                                                                        ml="auto"
                                                                    >
                                                                        Calculando...
                                                                    </Typography>
                                                                </Box>
                                                            ) : s.status ===
                                                              'error' ? (
                                                                <Tooltip
                                                                    title={
                                                                        s.error ||
                                                                        'Erro'
                                                                    }
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            display:
                                                                                'flex',
                                                                            alignItems:
                                                                                'center',
                                                                            gap: 1,
                                                                        }}
                                                                    >
                                                                        <Box
                                                                            sx={{
                                                                                width: 16,
                                                                                height: 16,
                                                                                borderRadius: 1,
                                                                                bgcolor:
                                                                                    'error.light',
                                                                                opacity: 0.4,
                                                                                flexShrink: 0,
                                                                            }}
                                                                        />
                                                                        <Typography
                                                                            variant="body2"
                                                                            color="text.secondary"
                                                                        >
                                                                            {
                                                                                label
                                                                            }
                                                                        </Typography>
                                                                        <Typography
                                                                            variant="caption"
                                                                            color="error"
                                                                            ml="auto"
                                                                        >
                                                                            Erro
                                                                        </Typography>
                                                                    </Box>
                                                                </Tooltip>
                                                            ) : (
                                                                <Box
                                                                    sx={{
                                                                        display:
                                                                            'flex',
                                                                        alignItems:
                                                                            'center',
                                                                        gap: 1,
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            width: 16,
                                                                            height: 16,
                                                                            borderRadius: 1,
                                                                            bgcolor:
                                                                                'action.disabledBackground',
                                                                            flexShrink: 0,
                                                                        }}
                                                                    />
                                                                    <Typography
                                                                        variant="body2"
                                                                        color="text.secondary"
                                                                    >
                                                                        {label}
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.disabled"
                                                                        ml="auto"
                                                                    >
                                                                        —
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    );
                                                }
                                            )}
                                        </Stack>
                                    </Paper>
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                    <Paper
                                        sx={{
                                            p: 2.5,
                                            height: '100%',
                                            borderRadius: 3,
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle2"
                                            fontWeight={700}
                                            color="text.secondary"
                                            gutterBottom
                                        >
                                            Frequência Ressonância
                                        </Typography>
                                        {freq ? (
                                            <Stack spacing={1.5}>
                                                {[
                                                    {
                                                        label: 'Canal 0',
                                                        key: 'channel0',
                                                        value: freq.channel0,
                                                        color: '#42a5f5',
                                                    },
                                                    {
                                                        label: 'Canal 1',
                                                        key: 'channel1',
                                                        value: freq.channel1,
                                                        color: '#ab47bc',
                                                    },
                                                ].map(
                                                    ({
                                                        label,
                                                        key,
                                                        value,
                                                        color,
                                                    }) => {
                                                        const st =
                                                            freqStats[key];
                                                        return (
                                                            <Box key={label}>
                                                                <Box
                                                                    sx={{
                                                                        display:
                                                                            'flex',
                                                                        justifyContent:
                                                                            'space-between',
                                                                        alignItems:
                                                                            'flex-start',
                                                                    }}
                                                                >
                                                                    <Box>
                                                                        <Typography
                                                                            variant="caption"
                                                                            color="text.secondary"
                                                                        >
                                                                            {
                                                                                label
                                                                            }
                                                                        </Typography>
                                                                        <Box
                                                                            sx={{
                                                                                display:
                                                                                    'flex',
                                                                                alignItems:
                                                                                    'baseline',
                                                                                gap: 0.5,
                                                                            }}
                                                                        >
                                                                            <Typography
                                                                                variant="h5"
                                                                                fontWeight={
                                                                                    800
                                                                                }
                                                                                fontFamily="monospace"
                                                                                sx={{
                                                                                    color,
                                                                                    lineHeight: 1.2,
                                                                                }}
                                                                            >
                                                                                {value !=
                                                                                null
                                                                                    ? (
                                                                                          value /
                                                                                          1e6
                                                                                      ).toFixed(
                                                                                          3
                                                                                      )
                                                                                    : '—'}
                                                                            </Typography>
                                                                            <Typography
                                                                                variant="caption"
                                                                                color="text.secondary"
                                                                            >
                                                                                MHz
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>
                                                                    <Sparkline
                                                                        data={
                                                                            freqHistory[
                                                                                key
                                                                            ]
                                                                        }
                                                                        color={
                                                                            color
                                                                        }
                                                                        width={
                                                                            70
                                                                        }
                                                                        height={
                                                                            28
                                                                        }
                                                                    />
                                                                </Box>
                                                                {st && (
                                                                    <Typography
                                                                        variant="caption"
                                                                        fontFamily="monospace"
                                                                        color="text.disabled"
                                                                        sx={{
                                                                            mt: 0.3,
                                                                            display:
                                                                                'block',
                                                                        }}
                                                                    >
                                                                        μ=
                                                                        {(
                                                                            st.mean /
                                                                            1e6
                                                                        ).toFixed(
                                                                            3
                                                                        )}{' '}
                                                                        σ=
                                                                        {(
                                                                            st.std /
                                                                            1e3
                                                                        ).toFixed(
                                                                            1
                                                                        )}{' '}
                                                                        kHz
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        );
                                                    }
                                                )}
                                                {freq.channel0 != null &&
                                                    freq.channel1 != null && (
                                                        <>
                                                            <Divider />
                                                            <MetricRow
                                                                label="Δ (Ch1 − Ch0)"
                                                                value={(
                                                                    (freq.channel1 -
                                                                        freq.channel0) /
                                                                    1e3
                                                                ).toFixed(1)}
                                                                unit="kHz"
                                                            />
                                                        </>
                                                    )}
                                            </Stack>
                                        ) : (
                                            <Typography
                                                variant="body2"
                                                color="text.disabled"
                                            >
                                                Sem dados
                                            </Typography>
                                        )}
                                    </Paper>
                                </Grid>

                                <Grid item xs={12}>
                                    <Paper
                                        sx={{
                                            p: 2.5,
                                            borderRadius: 3,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            maxHeight: logsMaxHeight || undefined,
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle2"
                                            fontWeight={700}
                                            color="text.secondary"
                                            gutterBottom
                                        >
                                            Últimos Logs
                                        </Typography>
                                        {diagnostics.length === 0 ? (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    py: 0.5,
                                                }}
                                            >
                                                <Lucide.Circle
                                                    size={8}
                                                    color={theme.palette.success.main}
                                                    fill={theme.palette.success.main}
                                                />
                                                <Typography
                                                    variant="body2"
                                                    color="success.main"
                                                >
                                                    Todos os parâmetros dentro
                                                    do normal
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    gap: 0.5,
                                                    mb: 1,
                                                }}
                                            >
                                                {diagnostics.map((d, idx) => (
                                                    <Chip
                                                        key={idx}
                                                        icon={
                                                            <Lucide.Circle
                                                                size={8}
                                                                fill="currentColor"
                                                            />
                                                        }
                                                        label={`${d.area}: ${d.msg}`}
                                                        size="small"
                                                        color={d.severity}
                                                        variant="outlined"
                                                        sx={{
                                                            height: 22,
                                                            fontSize: 11,
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        )}
                                        {eventLog.length > 0 && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    flex: 1,
                                                    minHeight: 0,
                                                }}
                                            >
                                                <Divider sx={{ my: 1 }} />
                                                <Typography
                                                    variant="caption"
                                                    color="text.disabled"
                                                    fontSize={9}
                                                    sx={{
                                                        mb: 0.5,
                                                        display: 'block',
                                                    }}
                                                >
                                                    HISTÓRICO (últimas{' '}
                                                    {eventLog.length})
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        flex: 1,
                                                        minHeight: 0,
                                                        overflow: 'auto',
                                                        '&::-webkit-scrollbar':
                                                            { width: 4 },
                                                        '&::-webkit-scrollbar-thumb':
                                                            {
                                                                bgcolor:
                                                                    'divider',
                                                                borderRadius: 2,
                                                            },
                                                    }}
                                                >
                                                    <Stack spacing={0.2}>
                                                        {[...eventLog]
                                                            .reverse()
                                                            .map((e, idx) => (
                                                                <Box
                                                                    key={idx}
                                                                    sx={{
                                                                        display:
                                                                            'flex',
                                                                        alignItems:
                                                                            'center',
                                                                        gap: 1,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="caption"
                                                                        fontFamily="monospace"
                                                                        color="text.disabled"
                                                                        sx={{
                                                                            minWidth: 52,
                                                                            flexShrink: 0,
                                                                        }}
                                                                    >
                                                                        {e.ts}
                                                                    </Typography>
                                                                    <Lucide.Circle
                                                                        size={6}
                                                                        color={theme.palette[e.severity]?.main}
                                                                        fill={theme.palette[e.severity]?.main}
                                                                        style={{ flexShrink: 0 }}
                                                                    />
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                        noWrap
                                                                    >
                                                                        {e.msg}
                                                                    </Typography>
                                                                </Box>
                                                            ))}
                                                    </Stack>
                                                </Box>
                                            </Box>
                                        )}
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Paper sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
                        <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            sx={{ mb: 2 }}
                        >
                            Espectrometria
                        </Typography>
                        <Grid container spacing={2}>
                            {[
                                { key: 'turbidimetry', title: 'Turbidimetria' },
                                { key: 'nephelometry', title: 'Nefelometria' },
                                { key: 'fluorescence', title: 'Fluorescência' },
                            ].map(({ key, title }) => {
                                const spectralData = device?.spectral?.[key];
                                if (!spectralData) return null;
                                const st = spectralSummary[key];
                                return (
                                    <Grid item xs={12} md={4} key={key}>
                                        <Box
                                            sx={{
                                                p: 2,
                                                bgcolor: alpha(
                                                    theme.palette.divider,
                                                    0.04
                                                ),
                                                borderRadius: 2,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                height: '100%',
                                            }}
                                        >
                                            <SpectralBarChart
                                                data={spectralData}
                                                title={title}
                                                showRef
                                            />
                                            {(st?.saturated ?? 0) > 0 && (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 0.5,
                                                        mt: 1.5,
                                                        flexWrap: 'wrap',
                                                    }}
                                                >
                                                    {st?.saturated > 0 && (
                                                        <Chip
                                                            icon={
                                                                <Lucide.TriangleAlert
                                                                    size={12}
                                                                />
                                                            }
                                                            label={`${st.saturated} sat.`}
                                                            size="small"
                                                            color="warning"
                                                            variant="outlined"
                                                            sx={{
                                                                height: 20,
                                                                fontSize: 10,
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Paper>

                    {device?.capacitiveSensor && (
                        <Paper sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
                            <Typography
                                variant="subtitle1"
                                fontWeight={700}
                                sx={{ mb: 2 }}
                            >
                                Frequência de Ressonância
                            </Typography>
                            <CapacitiveSweepChart capacitiveSensor={device.capacitiveSensor} />
                        </Paper>
                    )}

                    <Paper sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
                        <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            sx={{ mb: 2 }}
                        >
                            Telemetria Técnica
                        </Typography>
                        <Grid container spacing={2.5}>
                            {/* Fonte de alimentação — visível a todos; ocupa a
                                linha toda quando o diagnóstico de engenharia
                                (Peltier/NEMA/controle) está escondido por papel. */}
                            <Grid item xs={12} md={showEngineering ? 4 : 12}>
                                <Box
                                    sx={{
                                        p: 2,
                                        bgcolor: alpha(
                                            theme.palette.divider,
                                            0.04
                                        ),
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        height: '100%',
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        fontWeight={700}
                                        color="text.secondary"
                                        sx={{ display: 'block', mb: 1.5 }}
                                    >
                                        FONTE DE ALIMENTAÇÃO
                                    </Typography>
                                    <Stack spacing={0.8}>
                                        <MetricRow
                                            label="Tensão"
                                            value={power?.voltage?.toFixed(2)}
                                            unit="V"
                                        />
                                        <MetricRow
                                            label="Corrente Total"
                                            value={power?.current?.toFixed(2)}
                                            unit="A"
                                        />
                                        <Divider sx={{ opacity: 0.5 }} />
                                        <MetricRow
                                            label="Potência"
                                            value={watts?.toFixed(1)}
                                            unit="W"
                                            color={
                                                watts > 45
                                                    ? 'warning.main'
                                                    : watts > 60
                                                      ? 'error.main'
                                                      : 'primary'
                                            }
                                        />
                                    </Stack>
                                </Box>
                            </Grid>

                            {/* Diagnóstico de engenharia (Peltier/NEMA/controle):
                                só staff técnico (ADMIN/LAB). O dado trafega pra
                                todos; o gate é só de apresentação. */}
                            {showEngineering && (
                                <>
                            <Grid item xs={12} md={4}>
                                <Box
                                    sx={{
                                        p: 2,
                                        bgcolor: alpha(
                                            theme.palette.divider,
                                            0.04
                                        ),
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        height: '100%',
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        fontWeight={700}
                                        color="text.secondary"
                                        sx={{ display: 'block', mb: 1.5 }}
                                    >
                                        CORRENTES PELTIER E AGITADOR
                                    </Typography>
                                    <Stack spacing={0.8}>
                                        <MetricRow
                                            label="Peltier Amostra"
                                            value={peltier?.sampleChamber?.toFixed(
                                                2
                                            )}
                                            unit="A"
                                            color={
                                                peltier?.sampleChamber !=
                                                    null &&
                                                Math.abs(
                                                    peltier.sampleChamber
                                                ) > 3
                                                    ? 'warning.main'
                                                    : undefined
                                            }
                                        />
                                        <MetricRow
                                            label="Peltier Dissipador"
                                            value={peltier?.heatExchanger?.toFixed(
                                                2
                                            )}
                                            unit="A"
                                            color={
                                                peltier?.heatExchanger !=
                                                    null &&
                                                Math.abs(
                                                    peltier.heatExchanger
                                                ) > 3
                                                    ? 'warning.main'
                                                    : undefined
                                            }
                                        />
                                        <Divider sx={{ opacity: 0.5 }} />
                                        <MetricRow
                                            label="Agitador Bobina A"
                                            value={nema?.coilA?.toFixed(2)}
                                            unit="A"
                                        />
                                        <MetricRow
                                            label="Agitador Bobina B"
                                            value={nema?.coilB?.toFixed(2)}
                                            unit="A"
                                        />
                                    </Stack>
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Box
                                    sx={{
                                        p: 2,
                                        bgcolor: alpha(
                                            theme.palette.divider,
                                            0.04
                                        ),
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        height: '100%',
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        fontWeight={700}
                                        color="text.secondary"
                                        sx={{ display: 'block', mb: 1.5 }}
                                    >
                                        CONTROLE
                                    </Typography>
                                    <Stack spacing={0.8}>
                                        <MetricRow
                                            label="Erro Amostra"
                                            value={control?.sampleTempError?.toFixed(
                                                2
                                            )}
                                            unit="°C"
                                            color={
                                                control?.sampleTempError !=
                                                    null &&
                                                Math.abs(
                                                    control.sampleTempError
                                                ) > 2
                                                    ? 'warning.main'
                                                    : undefined
                                            }
                                        />
                                        <MetricRow
                                            label="Saída PID Amostra"
                                            value={control?.sampleTempU?.toFixed(
                                                2
                                            )}
                                        />
                                        <Divider sx={{ opacity: 0.5 }} />
                                        <MetricRow
                                            label="Erro Núcleo"
                                            value={control?.coreTempError?.toFixed(
                                                2
                                            )}
                                            unit="°C"
                                        />
                                        <MetricRow
                                            label="Saída PID Núcleo"
                                            value={control?.coreTempU?.toFixed(
                                                2
                                            )}
                                        />
                                    </Stack>
                                </Box>
                            </Grid>
                                </>
                            )}
                        </Grid>
                    </Paper>
                </>
            )}
            <Drawer
                anchor="right"
                open={calibDrawerOpen}
                onClose={() => setCalibDrawerOpen(false)}
            >
                <Box sx={{ width: { xs: '100vw', sm: 340 }, maxWidth: '100vw', p: 3 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 2,
                        }}
                    >
                        <Typography variant="h6" fontWeight={700}>
                            Configurar Sensor
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => setCalibDrawerOpen(false)}
                        >
                            <Lucide.X size={18} />
                        </IconButton>
                    </Box>

                    <Alert severity="warning" sx={{ mb: 2.5 }}>
                        Só tem efeito quando esta torre tem um tubo inserido
                        (é o tubo que dispara a leitura espectral). Uma vez
                        aplicado, o valor manual fica travado para essa
                        medição — inclusive em tubos trocados depois — até
                        você clicar em "Voltar para Automático".
                    </Alert>

                    <Stack spacing={2.5}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                            }}
                        >
                            <FormControl size="small" fullWidth>
                                <InputLabel id="sensor-measurement-label">
                                    Sensor
                                </InputLabel>
                                <Select
                                    labelId="sensor-measurement-label"
                                    label="Sensor"
                                    value={sensorMeasurement}
                                    onChange={(e) =>
                                        handleSensorMeasurementChange(
                                            e.target.value
                                        )
                                    }
                                >
                                    <MenuItem value="turbidimetry">
                                        Turbidimetria
                                    </MenuItem>
                                    <MenuItem value="nephelometry">
                                        Nefelometria
                                    </MenuItem>
                                    <MenuItem value="fluorescence">
                                        Fluorescência
                                    </MenuItem>
                                </Select>
                            </FormControl>
                            <Chip
                                size="small"
                                label={
                                    sensorModeIsManual
                                        ? 'Manual'
                                        : 'Automático'
                                }
                                color={
                                    sensorModeIsManual ? 'warning' : 'default'
                                }
                                variant={
                                    sensorModeIsManual ? 'filled' : 'outlined'
                                }
                                sx={{ flexShrink: 0 }}
                            />
                        </Box>

                        {(sensorMeasurement === 'nephelometry' ||
                            sensorMeasurement === 'fluorescence') && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Nefelometria e Fluorescência usam o mesmo
                                sensor físico — configurar uma afeta a outra.
                            </Typography>
                        )}

                        <TextField
                            size="small"
                            label="Ganho"
                            type="number"
                            fullWidth
                            value={sensorGain}
                            onChange={(e) => setSensorGain(e.target.value)}
                        />

                        <TextField
                            size="small"
                            label="Tempo de Integração (ms)"
                            type="number"
                            fullWidth
                            value={sensorIntegrationTimeMs}
                            onChange={(e) =>
                                setSensorIntegrationTimeMs(e.target.value)
                            }
                        />

                        <Stack direction="row" spacing={1.5}>
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={handleApplySensorConfig}
                                disabled={sensorConfigSaving}
                            >
                                {sensorConfigSaving
                                    ? 'Aplicando...'
                                    : 'Aplicar'}
                            </Button>
                            <Button
                                variant="outlined"
                                color="warning"
                                fullWidth
                                onClick={handleResetSensorAuto}
                                disabled={
                                    sensorConfigSaving || !sensorModeIsManual
                                }
                            >
                                Voltar para Automático
                            </Button>
                        </Stack>
                    </Stack>
                </Box>
            </Drawer>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );

    return (
        <Permitido
            cap="telemetria.ver"
            fallback={
                <Box p={4} textAlign="center">
                    <Alert severity="error" sx={{ mb: 2, display: 'inline-flex' }}>
                        Seu plano atual não permite o acesso a Dashboards Analíticos.
                    </Alert>
                    <br />
                    <Button variant="outlined" onClick={onBack}>
                        Voltar para a Lista
                    </Button>
                </Box>
            }
        >
            {dashboardContent}
        </Permitido>
    );
}
